/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { IconButton } from '@coursera/cds-core';
import { useVisibilityTracker } from '@coursera/event-pulse/react';

import TrackedDiv from 'bundles/page/components/TrackedDiv';
import type { AutocompleteMode } from 'bundles/search-common/constants/autocomplete';
import { removeRecentSearch } from 'bundles/search-common/utils/SearchUtils';
import type { AutocompleteItems } from 'bundles/search-common/utils/autocomplete';
import { getAutocompleteEventingData } from 'bundles/search-common/utils/autocomplete';

const styles = {
  optionContainer: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    position: relative;
    height: 24px;
    min-height: 24px;
    max-height: 24px;

    &:hover .clear-icon {
      opacity: 1;
    }

    /* On desktop (not mobile/tablet): Make button invisible by default, visible on hover */
    @media (min-width: 1061px) {
      &:not(:hover) .clear-icon-button {
        opacity: 0;
      }

      &:hover .clear-icon-button {
        opacity: 1;
      }
    }

    /* On mobile/tablet: Always visible */
    @media (max-width: 1060px) {
      .clear-icon-button {
        opacity: 1;
      }
    }
  `,

  clearIcon: css`
    opacity: 0;
    transition: opacity 0.2s ease;
    margin-right: calc(-1 * var(--cds-spacing-50));
    flex-shrink: 0;

    &:hover,
    &:focus {
      opacity: 1;
    }
  `,
};

type Props = {
  children: React.ReactNode;
  autocompleteState: AutocompleteMode;
  id: string;
  recentlyViewedItems: AutocompleteItems;
  suggestionIndex?: number;
  onRemove?: () => void;
};

const AutocompleteRecentlySearchedOption = ({
  children,
  autocompleteState,
  id,
  recentlyViewedItems,
  suggestionIndex,
  onRemove,
}: Props) => {
  const trackingData = getAutocompleteEventingData(id, autocompleteState, recentlyViewedItems, suggestionIndex);
  const trackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_autocomplete_suggestions',
    trackingData,
    [],
    { fullyVisible: true, initialDelay: 1000 }
  );

  // Extract search term from children (it's a string)
  const searchTerm = typeof children === 'string' ? children : '';

  const handleRemove = React.useCallback<React.MouseEventHandler<HTMLButtonElement>>(
    (e) => {
      e.stopPropagation();
      e.preventDefault();
      e.nativeEvent.stopImmediatePropagation();
      if (searchTerm) {
        removeRecentSearch(searchTerm);
        onRemove?.();
      }
    },
    [searchTerm, onRemove]
  );

  const handleMouseDown = React.useCallback<React.MouseEventHandler<HTMLButtonElement>>((e) => {
    e.stopPropagation();
    e.preventDefault();
    e.nativeEvent.stopImmediatePropagation();
  }, []);

  return (
    <div ref={trackingRef} css={styles.optionContainer}>
      <TrackedDiv withVisibilityTracking requireFullyVisible trackingName="autocomplete_item">
        {children}
      </TrackedDiv>
      <div
        role="button"
        tabIndex={-1}
        css={css`
          display: inline-flex;
          align-items: center;
          pointer-events: auto;
          height: 24px;
          flex-shrink: 0;
        `}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          e.nativeEvent.stopImmediatePropagation();
        }}
        onMouseDown={(e) => {
          e.stopPropagation();
          e.preventDefault();
          e.nativeEvent.stopImmediatePropagation();
        }}
        onMouseUp={(e) => {
          e.stopPropagation();
          e.preventDefault();
          e.nativeEvent.stopImmediatePropagation();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.stopPropagation();
            e.preventDefault();
            e.nativeEvent.stopImmediatePropagation();
          }
        }}
      >
        <IconButton
          intent="close"
          size="small"
          variant="ghostSecondary"
          onClick={handleRemove}
          onMouseDown={handleMouseDown}
          onMouseUp={(e) => {
            e.stopPropagation();
            e.preventDefault();
            e.nativeEvent.stopImmediatePropagation();
          }}
          onPointerDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
            e.nativeEvent.stopImmediatePropagation();
          }}
          aria-label={`Remove ${searchTerm} from recent searches`}
          data-testid="clear-recent-search"
          className="clear-icon-button"
          css={css`
            transition: opacity 0.2s ease;

            &:hover,
            &:focus {
              background-color: transparent !important;
            }
          `}
        />
      </div>
    </div>
  );
};

export default AutocompleteRecentlySearchedOption;
